import { z } from 'zod'
import { BehaviourConfig } from './behaviours'
import { BotDetail, BotState, EventKind, LogLevel, McAuthType } from './bot'

/**
 * Протокол постоянного WebSocket-канала API ↔ runner (docs/02 §6).
 * Канал инициирует runner; все сообщения — typed envelope с дискриминантом `t`.
 *
 * Схемы здесь — не «документация», а рантайм-валидация: и API, и runner
 * обязаны прогонять входящее через `RunnerToApi.parse` / `ApiToRunner.parse`,
 * иначе рассинхрон версий превращается в неотлаживаемые баги.
 */

// ─────────────────────────── спека бота ───────────────────────────

/**
 * Всё, что воркеру нужно, чтобы подключиться. Без секретов —
 * эта структура ходит по каналу и попадает в логи/дампы при отладке.
 */
export const BotSpec = z.object({
  botId: z.string().min(1),
  name: z.string().min(1),
  host: z.string().min(1),
  port: z.number().int().min(1).max(65535).default(25565),
  username: z.string().min(1),
  /** `"26.2"`, `"1.21.11"`… либо `null` = авто-проба версии по SLP-пингу. */
  version: z.string().min(1).nullable().default(null),
  authType: McAuthType,
  /** Команда после спавна, напр. `"/survival"` (docs/07 §4). */
  joinCommand: z.string().min(1).nullable().default(null),
  behaviours: BehaviourConfig.default({})
})
export type BotSpec = z.infer<typeof BotSpec>

/**
 * Секреты бота. Передаются воркеру **только** через IPC при инициализации
 * (docs/02 §4.1: не через env и не через argv — argv видно в `ps`) и никогда
 * не логируются. По каналу API→runner идут отдельно от `BotSpec`.
 */
export const BotSecrets = z.object({
  /**
   * Расшифрованный кеш профиля `prismarine-auth` (то, что в БД лежит как
   * `mcAccount.tokenCacheEnc`). `null` для OFFLINE-аккаунтов.
   */
  tokenCache: z.string().nullable().default(null),
  /**
   * Каталог (tmpfs), куда воркер материализует кеш для `prismarine-auth`
   * и который стирает сразу после логина (docs/07 §4 `wipeProfilesDir`).
   */
  profilesDir: z.string().min(1)
})
export type BotSecrets = z.infer<typeof BotSecrets>

/** Загрузка runner'а из heartbeat (docs/05 §2 `runner.load`). */
export const RunnerLoad = z.object({
  bots: z.number().int().min(0),
  cpu: z.number().min(0),
  rssMb: z.number().min(0)
})
export type RunnerLoad = z.infer<typeof RunnerLoad>

/**
 * Виды кадров камеры (docs/02 §4.3, docs/08).
 *
 * `entities` вместо апстримного `entity`: `WorldView` пересылает события про
 * каждую сущность по отдельности, а runner собирает снимок окрестностей —
 * почему именно так, написано у `ViewerEntitiesFrame` в `viewer.ts`.
 */
export const VIEWER_FRAME_KINDS = [
  'chunk',
  'unloadChunk',
  'entities',
  'position',
  'blockUpdate'
] as const
export const ViewerFrameKind = z.enum(VIEWER_FRAME_KINDS)
export type ViewerFrameKind = z.infer<typeof ViewerFrameKind>

// ──────────────────────── runner → API ────────────────────────

export const RunnerHello = z.object({
  t: z.literal('hello'),
  runnerId: z.string().min(1),
  version: z.string().min(1),
  /**
   * Фича-флаги ядра (docs/02 §6 упоминает `capabilities` в HELLO):
   * напр. `['mc:26.2', 'viewer', 'pathfinder']`.
   */
  capabilities: z.array(z.string()).default([]),
  maxBots: z.number().int().min(0),
  arch: z.string().min(1)
})

export const RunnerHeartbeat = z.object({
  t: z.literal('heartbeat'),
  ts: z.number(),
  load: RunnerLoad
})

export const RunnerStateMsg = z.object({
  t: z.literal('state'),
  botId: z.string().min(1),
  state: BotState,
  /**
   * Человекочитаемая причина перехода («kicked: рестарт», «повтор через 45 с»).
   * UI показывает её в бейдже состояния, поэтому она едет вместе со статусом,
   * а не отдельным событием — иначе в интерфейсе статус и причина расходятся.
   */
  reason: z.string().max(500).optional(),
  detail: BotDetail.optional(),
  at: z.number()
})

export const RunnerEventMsg = z.object({
  t: z.literal('event'),
  botId: z.string().min(1),
  level: LogLevel,
  kind: EventKind,
  msg: z.string(),
  data: z.unknown(),
  at: z.number()
})

export const RunnerAuthCode = z.object({
  t: z.literal('authCode'),
  accountId: z.string().min(1),
  userCode: z.string().min(1),
  uri: z.string().url(),
  expiresAt: z.number()
})

export const RunnerAuthOk = z.object({
  t: z.literal('authOk'),
  accountId: z.string().min(1),
  gamertag: z.string(),
  uuid: z.string(),
  /** Кеш профиля в открытом виде — API шифрует его envelope'ом сразу по приёму. */
  cacheBlob: z.string()
})

/**
 * Device-code вход провалился уже после того, как runner подтвердил приём
 * команды (`ack`).
 *
 * Дополнение к docs/02 §6, которого требует реальность: API ждёт `ack` на
 * `authBegin` десять секунд (`AUTH_BEGIN_ACK_MS`), а живой вход занимает минуты
 * — значит `ack` физически не может нести исход потока. Без этого сообщения
 * истёкший или отклонённый код оставлял бы аккаунт в `AUTH_PENDING` навсегда,
 * а docs/01 US-2 AC4 требует «понятная ошибка и кнопка повторить».
 */
export const RunnerAuthFail = z.object({
  t: z.literal('authFail'),
  accountId: z.string().min(1),
  /** Причина по-русски: она уезжает в `mcAccount.lastAuthError` и в UI. */
  reason: z.string().min(1).max(500)
})

/**
 * Кадр камеры. Едет **бинарным** WS-сообщением, целиком упакованным msgpack'ом
 * (ADR-07: «msgpack для кадров камеры»): `payload` тяжёлых видов —
 * это Uint8Array, который в JSON-конверте превратился бы в `{"0":12,…}`.
 */
export const RunnerFrame = z.object({
  t: z.literal('frame'),
  sessionId: z.string().min(1),
  kind: ViewerFrameKind,
  payload: z.unknown()
})

/**
 * Кадр мини-карты (L1, docs/08 §4). Отдельное сообщение, а не `frame`:
 * у мини-карты нет viewer-сессии — она привязана к боту и раздаётся в комнату
 * `bot:<botId>`, пока там есть хоть один подписчик.
 */
export const RunnerMinimap = z.object({
  t: z.literal('minimap'),
  botId: z.string().min(1),
  originX: z.number().int(),
  originZ: z.number().int(),
  baseY: z.number().int(),
  data: z.instanceof(Uint8Array)
})

/**
 * Сессии камеры закрылись на стороне runner'а: бот остановился, ушёл на
 * реконнект или его воркер упал.
 *
 * Дополнение к docs/02 §6: без него строка `ViewerSession` оставалась бы
 * открытой навсегда — она закрывается либо клиентом, либо таймаутом heartbeat,
 * а «бот умер» не наступает ни там, ни там. docs/08 §5 требует причину
 * `bot-stopped`, и прийти она может только отсюда.
 */
export const RunnerViewerClosed = z.object({
  t: z.literal('viewerClosed'),
  sessionIds: z.array(z.string().min(1)),
  reason: z.string().min(1).max(200)
})

export const RunnerAck = z.object({
  t: z.literal('ack'),
  commandId: z.string().min(1),
  ok: z.boolean(),
  error: z.string().optional()
})

/**
 * Кеш профиля обновился при обычном входе бота (docs/02 §4.2).
 *
 * Отличие от `authOk`: там результат device-code входа, он меняет статус
 * аккаунта и его гамертег. Здесь только токены: `prismarine-auth` продлил их
 * сам, и если не сохранить, каждый следующий старт воркера снова пойдёт в
 * `login_with_xbox` — за что Mojang отвечает `429` и весь флот встаёт.
 *
 * Адресуемся ботом, а не аккаунтом: воркер знает `botId`, привязку к аккаунту
 * держит API.
 */
export const RunnerCacheRefresh = z.object({
  t: z.literal('cacheRefresh'),
  botId: z.string().min(1),
  /** Открытый blob — API шифрует его сразу по приёму и нигде не логирует. */
  cacheBlob: z.string().min(1)
})

export const RunnerToApi = z.discriminatedUnion('t', [
  RunnerHello,
  RunnerHeartbeat,
  RunnerStateMsg,
  RunnerEventMsg,
  RunnerAuthCode,
  RunnerAuthOk,
  RunnerAuthFail,
  RunnerCacheRefresh,
  RunnerFrame,
  RunnerMinimap,
  RunnerViewerClosed,
  RunnerAck
])
export type RunnerToApi = z.infer<typeof RunnerToApi>
export type RunnerToApiKind = RunnerToApi['t']

// ──────────────────────── API → runner ────────────────────────

export const ApiWelcome = z.object({
  t: z.literal('welcome'),
  assignments: z.array(BotSpec)
})

export const ApiStart = z.object({
  t: z.literal('start'),
  commandId: z.string().min(1),
  spec: BotSpec
})

export const ApiStop = z.object({
  t: z.literal('stop'),
  commandId: z.string().min(1),
  botId: z.string().min(1),
  reason: z.string().optional()
})

export const ApiChat = z.object({
  t: z.literal('chat'),
  commandId: z.string().min(1),
  botId: z.string().min(1),
  message: z.string().min(1)
})

export const ApiAuthBegin = z.object({
  t: z.literal('authBegin'),
  commandId: z.string().min(1),
  accountId: z.string().min(1),
  flow: z.literal('msa')
})

export const ApiViewerOpen = z.object({
  t: z.literal('viewerOpen'),
  commandId: z.string().min(1),
  botId: z.string().min(1),
  sessionId: z.string().min(1),
  /** docs/06 §6: 2..8, по умолчанию 4 (ADR-10). */
  viewDistance: z.number().int().min(2).max(8)
})

/**
 * Начать прогрузку мира в открытой сессии.
 *
 * Разделение `viewerOpen` → `viewerStart` — правка архитектуры docs/08 §3.1,
 * без которой камера физически не работает. `WorldView.init()` вываливает **все**
 * колонки радиуса за десятки миллисекунд, и в схеме документа это происходит
 * внутри обработки `viewerOpen`, то есть до того, как REST успел ответить
 * клиенту `sessionId`, а клиент — подписаться на комнату. Весь стартовый мир
 * улетал в пустую комнату, а следующих чанков у стоящего бота не бывает.
 *
 * Поэтому `viewerOpen` только создаёт `WorldView` и подписки, а спираль колонок
 * запускает `viewerStart`, который API отправляет в момент, когда в комнате
 * появился первый подписчик. Побочная выгода: клиент, который так и не
 * подписался, не стоит ни одного байта.
 */
export const ApiViewerStart = z.object({
  t: z.literal('viewerStart'),
  commandId: z.string().min(1),
  sessionId: z.string().min(1)
})

export const ApiViewerClose = z.object({
  t: z.literal('viewerClose'),
  commandId: z.string().min(1),
  sessionId: z.string().min(1)
})

/**
 * Смена радиуса прогрузки на живой сессии (docs/06 §8 `viewer.setViewDistance`).
 *
 * Дополнение к docs/02 §6, где для камеры есть только open/close: без отдельной
 * команды слайдер vd пришлось бы реализовать как «закрыть и открыть заново», то
 * есть выбросить весь прогруженный мир и заново прокачать полмегабайта.
 */
export const ApiViewerSetDistance = z.object({
  t: z.literal('viewerSetDistance'),
  commandId: z.string().min(1),
  sessionId: z.string().min(1),
  viewDistance: z.number().int().min(2).max(8)
})

/**
 * Включить/выключить мини-карту бота (L1). Команда без `commandId`: это не
 * действие оператора, а следствие того, что в комнате `bot:<botId>` появился
 * или исчез подписчик. Ack не нужен — потерянная команда исправится следующим
 * `subscribe`, а лишние 4 КБ/с трафика того не стоят.
 */
export const ApiMinimap = z.object({
  t: z.literal('minimap'),
  botId: z.string().min(1),
  enabled: z.boolean()
})

export const ApiReconcile = z.object({
  t: z.literal('reconcile'),
  specs: z.array(BotSpec)
})

/**
 * Доставка расшифрованных секретов бота.
 *
 * Отдельным сообщением, а НЕ полем в `BotSpec`, сознательно: спека логируется
 * и уходит в события, а секрет не должен попасть ни в лог, ни в ring buffer.
 * API присылает это сообщение перед `start` (или в ответ на запрос воркера),
 * runner держит секрет только в памяти и передаёт воркеру в `init`.
 *
 * Без него MSA-боты стартовать не могут: воркер уходит в `AUTH_FAILED`
 * с причиной «нужен re-auth», а OFFLINE-боты работают полностью.
 */
export const ApiSecrets = z.object({
  t: z.literal('secrets'),
  botId: z.string().min(1),
  secrets: BotSecrets
})

export const ApiToRunner = z.discriminatedUnion('t', [
  ApiWelcome,
  ApiStart,
  ApiStop,
  ApiChat,
  ApiSecrets,
  ApiAuthBegin,
  ApiViewerOpen,
  ApiViewerStart,
  ApiViewerClose,
  ApiViewerSetDistance,
  ApiMinimap,
  ApiReconcile
])
export type ApiToRunner = z.infer<typeof ApiToRunner>
export type ApiToRunnerKind = ApiToRunner['t']

// ──────────────────────────── константы ───────────────────────────

/** docs/02 §6: heartbeat каждые 5 с, 3 пропуска → runner OFFLINE. */
export const RUNNER_HEARTBEAT_INTERVAL_MS = 5_000
export const RUNNER_MISSED_HEARTBEATS_BEFORE_OFFLINE = 3
export const RUNNER_OFFLINE_AFTER_MS =
  RUNNER_HEARTBEAT_INTERVAL_MS * RUNNER_MISSED_HEARTBEATS_BEFORE_OFFLINE

/** Кольцевой буфер событий в runner'е, пока API недоступен (docs/02 §8). */
export const RUNNER_EVENT_RING_BUFFER = 10_000

/**
 * Лимит одного сообщения на канале runner ↔ API.
 *
 * События бота — килобайты, но кадр камеры это целая колонка мира: 24 секции
 * × 4096 индексов = ~100 КБ, а при 16-битных индексах вдвое больше. 1 МиБ даёт
 * запас и всё ещё не позволяет одному сообщению съесть память API.
 */
export const RUNNER_MAX_FRAME_BYTES = 1024 * 1024
